import { State, h, Component, Prop, Element, Build, Watch, Method } from '@stencil/core';
import { addAxisLabelsToXYChart, parseValueToNumberMultiSeries } from '../chartUtils';

import * as am5 from '@amcharts/amcharts5/index';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { parseValueToNumber } from '../chartUtils';
function isValidDateString(dateString: string): boolean {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}
const generateMockData = () => {
  // Generate random data
  let date = new Date();
  date.setHours(0, 0, 0, 0);
  let value = 100;

  function generateData() {
    value = Math.round(Math.random() * 10 - 5 + value);
    am5.time.add(date, 'day', 1);
    return {
      date: date.getTime(),
      value: value,
    };
  }

  function generateDatas(count) {
    let data = [];
    for (var i = 0; i < count; ++i) {
      data.push(generateData());
    }
    return data;
  }
  return generateDatas(1200);
};
@Component({
  tag: 'udp-line-chart',
  styleUrl: 'udp-line-chart.css',
  shadow: true,
})
export class UdpLineChart {
  @Element() el: HTMLElement;

  @Prop() data: { [key: string]: any }[];
  @Prop() xAxisField: string = Build.isDev ? 'date' : undefined;
  @Prop() yAxisField: string = Build.isDev ? 'value' : undefined;
  @Prop() title: string = Build.isDev ? 'Test header' : undefined;
  @Prop() subheader: string = Build.isDev ? 'Test SubHeader' : undefined;
  @Prop() size: 'small' | 'medium' | 'large' = 'medium';
  @Prop() kpis: { value: string; label: string }[] = Build.isDev
    ? [
        { value: Math.round(Math.random() * 2000).toString(), label: 'YTD' },
        { value: Math.round(Math.random() * 2000).toString(), label: 'YTD' },
        { value: Math.round(Math.random() * 2000).toString(), label: 'YTD' },
      ]
    : undefined;
  @Prop() xLabel: string = Build.isDev ? 'Test X Label' : undefined;
  @Prop() yLabel: string = Build.isDev ? 'Test Y Label' : undefined;
  @Prop() customWidth?: number; // Rather then setting a size you can set a custom width
  @Prop() customHeight?: number; // Rather then setting a size you can set a custom height
  @Prop() customCardWidth?: number; // Rather then setting a size you can set a custom card width
  @Prop() customCardStyle?: { [key: string]: string } = {};
  @Prop() kpisPosition: 'left' | 'top' = 'left';
  @Prop() hideAdornment: boolean = false;
  @Prop() hideScrollbarX: boolean = false;
  @Prop() valueYFields: { fieldName: string; fieldLabel: string }[] = undefined;
  @Prop() showBullets: boolean = false;
  private chartId = 'udp-line-chart';
  private chartRoot: am5.Root;
  @State() transformedData: { [key: string]: any }[];

  componentDidLoad() {
    this.transformData();
  }
  @Watch('xAxisField')
  @Watch('yAxisField')
  @Watch('data')
  @Watch('xLabel')
  @Watch('yLabel')
  @Watch('valueYFields')
  transformData() {
    this.transformedData = Build.isDev
      ? generateMockData()
      : this.data
          ?.map(row => ({
            ...row,
            [this.xAxisField]:
              typeof row?.[this.xAxisField] === 'string' && isValidDateString(row?.[this.xAxisField]) ? new Date(row?.[this.xAxisField]).getTime() : row?.[this.xAxisField],
            ...(this.valueYFields?.length > 0 ? parseValueToNumberMultiSeries(this.valueYFields, row) : { [this.yAxisField]: parseValueToNumber(row?.[this.yAxisField]) }),
          }))
          .sort((a, b) => a[this.xAxisField] - b[this.xAxisField]);
  }

  @Method()
  makeSeries(chart, xAxis, yAxis, serieName, fieldName, root, singleSerie) {
    const series = chart.series.push(
      am5xy.LineSeries.new(this.chartRoot, {
        name: serieName,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: fieldName,
        valueXField: this.xAxisField,
        tooltip: am5.Tooltip.new(this.chartRoot, {
          labelText: singleSerie ? '{valueY}' : '{name}: {valueY}',
        }),
      }),
    );

    if (this.showBullets) {
      series.bullets.push(function () {
        return am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 5,
            fill: series.get('fill'),
          }),
        });
      });
    }

    series.data.setAll(this.transformedData);
    
    // Make stuff animate on load
    // https://www.amcharts.com/docs/v5/concepts/animations/
    series.appear();

  }

  @Watch('transformedData')
  @Watch('showBullets')
  async renderChart() {
    // sometimes the element cannot be found right away so loop umtil it loads
    if (this.el?.shadowRoot?.getElementById?.(this.chartId) == null) {
      await setTimeout(this.renderChart, 50);
    }

    if (this.chartRoot) {
      this.chartRoot.dispose();
    }

    if (!this.xAxisField || (!this.yAxisField && (!this.valueYFields || this.valueYFields?.length === 0))) return;
    this.chartRoot = am5.Root.new(this.el.shadowRoot.getElementById(this.chartId));

    // Set themes
    // https://www.amcharts.com/docs/v5/concepts/themes/
    this.chartRoot.setThemes([am5themes_Animated.new(this.chartRoot)]);

    // Create chart
    // https://www.amcharts.com/docs/v5/charts/xy-chart/
    var chart = this.chartRoot.container.children.push(
      am5xy.XYChart.new(this.chartRoot, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
        paddingLeft: 0,
        layout: this.chartRoot.verticalLayout,
      }),
    );

    // Add cursor
    // https://www.amcharts.com/docs/v5/charts/xy-chart/cursor/
    var cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(this.chartRoot, {
        behavior: 'none',
      }),
    );
    cursor.lineY.set('visible', false);

    // Create axes
    // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
    var xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(this.chartRoot, {
        maxDeviation: 0.2,
        baseInterval: {
          timeUnit: 'day',
          count: 1,
        },
        renderer: am5xy.AxisRendererX.new(this.chartRoot, {
          minorGridEnabled: true,
        }),
        tooltip: am5.Tooltip.new(this.chartRoot, {}),
      }),
    );

    var yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(this.chartRoot, {
        renderer: am5xy.AxisRendererY.new(this.chartRoot, {
          pan: 'zoom',
        }),
      }),
    );
    addAxisLabelsToXYChart(this.chartRoot, chart, this.xLabel, this.yLabel);

    // Add series
    // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
    if (this.valueYFields?.length > 0) {
      var legend = chart.children.push(
        am5.Legend.new(this.chartRoot, {
          centerX: am5.p50,
          x: am5.p50,
          marginTop: 15,
          marginBottom: 15,
        }),
      );

      this.valueYFields.forEach(field => {
        this.makeSeries(chart, xAxis, yAxis, field.fieldLabel, field.fieldName, this.chartRoot, false);
      });
      legend.data.setAll(chart.series.values);
    } else {
      this.makeSeries(chart, xAxis, yAxis, 'Series 1', this.yAxisField, this.chartRoot, true);
    }
    
    // Add scrollbar
    // https://www.amcharts.com/docs/v5/charts/xy-chart/scrollbars/
    if (!this.hideScrollbarX) {
      chart.set(
        'scrollbarX',
        am5.Scrollbar.new(this.chartRoot, {
          orientation: 'horizontal',
        }),
      );
    }

    // Make stuff animate on load
    // https://www.amcharts.com/docs/v5/concepts/animations/
    chart.appear(1000, 100);
  }

  disconnectedCallback() {
    // Clean up chart instance on component removal
    if (this.chartRoot) {
      this.chartRoot.dispose();
    }
  }
  render() {
    return (
      <chart-container
        adjustChartWidthForKpis
        title={this.title}
        subheader={this.subheader}
        size={this.size}
        chartId={this.chartId}
        kpis={this.kpis}
        customWidth={this.customWidth}
        customHeight={this.customHeight}
        customCardWidth={this.customCardWidth}
        customCardStyle={this.customCardStyle}
        kpisPosition={this.kpisPosition}
        hideAdornment={this.hideAdornment}
      />
    );
  }
}
